// web-admin/src/pages/sm/AttendancePenaltyManager.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  Card,
  CardContent
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import GavelIcon from "@mui/icons-material/Gavel";
import SecurityIcon from "@mui/icons-material/Security";
import axios from "axios";
import AppHeader from "../../components/AppHeader";

export default function AttendancePenaltyManager({ setIsLoggedIn }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [formData, setFormData] = useState({
    policy_name: "Standard Attendance Violation Policy",
    missed_clock_in_fine: 3000,
    missed_clock_out_fine: 3000,
    missed_both_fine: 5000,
    threshold_type: "LUMP_SUM_SURCHARGE",
    threshold_count: 3,
    threshold_amount: 15000
  });

  const getAuthHeader = () => {
    const tenant = localStorage.getItem("tenant") || "LHS";
    const token = localStorage.getItem("token");
    const headers = { "X-Tenant": tenant };
    if (token && token !== "null") {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  };

  useEffect(() => {
    fetchPolicy();
  }, []);

  const fetchPolicy = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL1}/sm/attendance-penalty/policy`,
        { withCredentials: true, headers: getAuthHeader() }
      );
      if (res.data && res.data.success && res.data.policy) {
        setFormData({
          policy_name: res.data.policy.policy_name || "Standard Attendance Violation Policy",
          missed_clock_in_fine: res.data.policy.missed_clock_in_fine || 3000,
          missed_clock_out_fine: res.data.policy.missed_clock_out_fine || 3000,
          missed_both_fine: res.data.policy.missed_both_fine || 5000,
          threshold_type: res.data.policy.threshold_type || "LUMP_SUM_SURCHARGE",
          threshold_count: res.data.policy.threshold_count || 3,
          threshold_amount: res.data.policy.threshold_amount || 15000
        });
      }
    } catch (err) {
      console.error("Fetch penalty policy error:", err);
      setMessage({ type: "error", text: "Failed to load attendance penalty policy settings." });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setMessage({ type: "", text: "" });
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL1}/sm/attendance-penalty/policy`,
        formData,
        { withCredentials: true, headers: getAuthHeader() }
      );
      if (res.data && res.data.success) {
        setMessage({ type: "success", text: "Attendance penalty policy saved successfully!" });
      } else {
        setMessage({ type: "error", text: res.data.message || "Failed to save policy." });
      }
    } catch (err) {
      console.error("Save penalty policy error:", err);
      setMessage({ type: "error", text: "Error saving policy settings." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppHeader setIsLoggedIn={setIsLoggedIn} />
      <Box sx={{ p: 3, maxWidth: "1600px", mx: "auto" }}>
        <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <GavelIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" fontWeight="bold">
            Attendance Violation Penalty Policy Settings
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Configure pre-defined fixed penalty fines for staff who forget to clock in or out, plus monthly threshold escalation rules.
        </Typography>
      </Paper>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}

      <form onSubmit={handleSave}>
        <Grid container spacing={3}>
          {/* Base Fines Section */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                  1. Standard Per-Occurrence Fines
                </Typography>
                <Divider sx={{ mb: 2.5 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Policy Name"
                      value={formData.policy_name}
                      onChange={(e) => setFormData({ ...formData, policy_name: e.target.value })}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Missed Clock-In Fine"
                      type="number"
                      value={formData.missed_clock_in_fine}
                      onChange={(e) => setFormData({ ...formData, missed_clock_in_fine: e.target.value })}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">MMK</InputAdornment>
                      }}
                      helperText="Applied when staff forgets Clock In"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Missed Clock-Out Fine"
                      type="number"
                      value={formData.missed_clock_out_fine}
                      onChange={(e) => setFormData({ ...formData, missed_clock_out_fine: e.target.value })}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">MMK</InputAdornment>
                      }}
                      helperText="Applied when staff forgets Clock Out"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Missed Both (Clock In & Out) Fine"
                      type="number"
                      value={formData.missed_both_fine}
                      onChange={(e) => setFormData({ ...formData, missed_both_fine: e.target.value })}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">MMK</InputAdornment>
                      }}
                      helperText="Applied when both clock records are missing for a worked day"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Threshold Escalation Section */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <SecurityIcon color="secondary" />
                  <Typography variant="h6" fontWeight="bold" color="secondary">
                    2. Monthly Threshold Escalation Rule
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2.5 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Threshold Rule Type</InputLabel>
                      <Select
                        value={formData.threshold_type}
                        label="Threshold Rule Type"
                        onChange={(e) => setFormData({ ...formData, threshold_type: e.target.value })}
                      >
                        <MenuItem value="LUMP_SUM_SURCHARGE">Lump-Sum Surcharge (Extra Fine when count exceeds X)</MenuItem>
                        <MenuItem value="GRACE_COUNT">Grace Allowance (First X violations are FREE)</MenuItem>
                        <MenuItem value="NONE">None (Standard Fines Only)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {formData.threshold_type !== "NONE" && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Threshold Violation Count (X)"
                          type="number"
                          value={formData.threshold_count}
                          onChange={(e) => setFormData({ ...formData, threshold_count: e.target.value })}
                          helperText="Number of missed clock occurrences per month"
                        />
                      </Grid>

                      {formData.threshold_type === "LUMP_SUM_SURCHARGE" && (
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Extra Surcharge Penalty Amount (XXXX)"
                            type="number"
                            value={formData.threshold_amount}
                            onChange={(e) => setFormData({ ...formData, threshold_amount: e.target.value })}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">MMK</InputAdornment>
                            }}
                            helperText="Extra fine added to payslip when violations count exceeds X"
                          />
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? "Saving Policy..." : "Save Penalty Policy"}
          </Button>
        </Box>
      </form>
    </Box>
  </Box>
  );
}
